import apiClient from './client';
import type { WebAnalytics, WebAnalyticsError } from '../types/webAnalytics';

/**
 * Client for the GA4 web-traffic analytics endpoint.
 *
 * One round trip returns the summary, daily series and every breakdown
 * table for a single window. The backend clamps `days` to [1, 730] and
 * caches each window for ~30 minutes. Pass `refresh` to bypass the cache.
 */
export async function getWebAnalytics(
  days: number = 30,
  refresh: boolean = false
): Promise<WebAnalytics> {
  const { data } = await apiClient.get<WebAnalytics>(
    '/admin/web/analytics/',
    { params: { days, ...(refresh ? { refresh: 1 } : {}) } }
  );
  return data;
}

/**
 * Turn a failed request into a human-readable message. The backend sends a
 * structured body for the two expected failure modes (not configured /
 * GA4 unreachable); anything else falls back to the generic axios message.
 */
export function describeWebAnalyticsError(err: unknown): string {
  const body = (err as { response?: { data?: Partial<WebAnalyticsError> } })
    ?.response?.data;
  if (body?.error === 'ga4_not_configured') {
    return `Google Analytics is not configured on the server (${body.detail ?? 'missing credentials'}).`;
  }
  if (body?.error === 'ga4_unavailable') {
    return `Google Analytics could not be reached: ${body.detail ?? 'unknown error'}`;
  }
  return err instanceof Error ? err.message : 'Failed to load web analytics';
}
